import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { parseTime, fmtTime, fmtPace, DISTANCES, riegel, trainingZonesFrom5k, mileageProgression, QUIZ, hrZones, runCalories, raceSplits, bmiOf, fuelingPlan } from "@/lib/tools";
import { TOOL_LIST } from "@/pages/ToolsIndex";
import { ToolEmailCapture, GoDeeper } from "@/components/ToolExtras";
import TOOLS_MANIFEST from "@/lib/toolsManifest.json";

const PACK_COMPONENTS = {
  "runner-tdee-calculator": lazy(() => import("@/components/tools/RunnerTdeeCalculator")),
  "runner-macro-calculator": lazy(() => import("@/components/tools/RunnerMacroCalculator")),
  "runner-protein-calculator": lazy(() => import("@/components/tools/RunnerProteinCalculator")),
  "body-composition-calculator": lazy(() => import("@/components/tools/BodyCompositionCalculator")),
  "one-rep-max-calculator": lazy(() => import("@/components/tools/OneRepMaxCalculator")),
  "body-recomp-calculator": lazy(() => import("@/components/tools/BodyRecompCalculator")),
  "recovery-readiness-score": lazy(() => import("@/components/tools/RecoveryReadinessScore")),
  "runner-longevity-score": lazy(() => import("@/components/tools/RunnerLongevityScore")),
  "hyrox-finish-time": lazy(() => import("@/components/tools/HyroxFinishTimePredictor")),
  "race-fueling-planner": lazy(() => import("@/components/tools/RaceFuelingPlanner")),
};

const inputCls = "w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200";
const selectCls = "bg-night border border-white/20 text-white font-mono-label px-4 py-3 focus:border-white transition-colors duration-200";
const label = "font-mono-label text-white/40 block mb-2";

const ResultRow = ({ k, v, testid }) => (
  <div className="flex items-baseline justify-between border-b border-white/10 py-4" data-testid={testid}>
    <span className="font-mono-label text-white/50">{k}</span>
    <span className="font-display text-2xl lg:text-3xl">{v}</span>
  </div>
);

const PaceCalculator = () => {
  const [dist, setDist] = useState("5K");
  const [custom, setCustom] = useState("");
  const [unit, setUnit] = useState("km");
  const [time, setTime] = useState("");
  const seconds = parseTime(time);
  const km = dist === "Custom" ? (parseFloat(custom) || 0) * (unit === "mi" ? 1.60934 : 1) : DISTANCES[dist];
  const valid = seconds && km > 0;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <span className={label}>DISTANCE</span>
          <select data-testid="pace-distance-select" value={dist} onChange={(e) => setDist(e.target.value)} className={`${selectCls} w-full`}>
            {[...Object.keys(DISTANCES), "Custom"].map((d) => <option key={d}>{d}</option>)}
          </select>
          {dist === "Custom" && (
            <div className="flex gap-3 mt-3">
              <input data-testid="pace-custom-distance" type="number" min="0" step="0.1" placeholder="DISTANCE" value={custom} onChange={(e) => setCustom(e.target.value)} className={inputCls} />
              <select value={unit} onChange={(e) => setUnit(e.target.value)} className={selectCls}>
                <option value="km">KM</option>
                <option value="mi">MI</option>
              </select>
            </div>
          )}
        </div>
        <div>
          <span className={label}>FINISH TIME (HH:MM:SS OR MM:SS)</span>
          <input data-testid="pace-time-input" placeholder="25:30" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
      </div>
      {valid && (
        <div className="mt-10" data-testid="pace-results">
          <ResultRow k="PACE PER MILE" v={`${fmtPace(seconds / (km / 1.60934))} /mi`} testid="pace-per-mile" />
          <ResultRow k="PACE PER KM" v={`${fmtPace(seconds / km)} /km`} testid="pace-per-km" />
          <ResultRow k="SPEED" v={`${((km / 1.60934) / (seconds / 3600)).toFixed(2)} mph`} testid="pace-speed" />
        </div>
      )}
    </div>
  );
};

const RacePredictor = () => {
  const [dist, setDist] = useState("5K");
  const [time, setTime] = useState("");
  const seconds = parseTime(time);
  const d1 = DISTANCES[dist];
  const targets = ["5K", "10K", "Half Marathon", "Marathon"];
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <span className={label}>RECENT RACE</span>
          <select data-testid="predictor-distance-select" value={dist} onChange={(e) => setDist(e.target.value)} className={`${selectCls} w-full`}>
            {Object.keys(DISTANCES).map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <span className={label}>FINISH TIME</span>
          <input data-testid="predictor-time-input" placeholder="24:00" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
      </div>
      {seconds && (
        <div className="mt-10" data-testid="predictor-results">
          {targets.filter((t) => t !== dist).map((t) => {
            const pred = riegel(seconds, d1, DISTANCES[t]);
            return <ResultRow key={t} k={t.toUpperCase()} v={fmtTime(pred)} testid={`predictor-${t.replace(/\s/g, "-").toLowerCase()}`} />;
          })}
          <p className="text-white/40 text-xs mt-6">Riegel formula, exponent 1.06. Assumes equivalent training for the target distance.</p>
        </div>
      )}
    </div>
  );
};

const PaceConverter = () => {
  const [pace, setPace] = useState("");
  const [unit, setUnit] = useState("mi");
  const sec = parseTime(pace);
  const perMi = unit === "mi" ? sec : sec * 1.60934;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <span className={label}>PACE (MM:SS)</span>
          <input data-testid="converter-pace-input" placeholder="9:30" value={pace} onChange={(e) => setPace(e.target.value)} className={inputCls} />
        </div>
        <div>
          <span className={label}>PER</span>
          <select data-testid="converter-unit-select" value={unit} onChange={(e) => setUnit(e.target.value)} className={`${selectCls} w-full`}>
            <option value="mi">MILE</option>
            <option value="km">KILOMETER</option>
          </select>
        </div>
      </div>
      {sec && (
        <div className="mt-10" data-testid="converter-results">
          <ResultRow k="PER MILE" v={`${fmtPace(perMi)} /mi`} testid="converter-per-mile" />
          <ResultRow k="PER KM" v={`${fmtPace(perMi / 1.60934)} /km`} testid="converter-per-km" />
          <ResultRow k="SPEED" v={`${(3600 / perMi).toFixed(2)} mph`} testid="converter-mph" />
          <ResultRow k="5K AT THIS PACE" v={fmtTime((perMi / 1.60934) * 5)} testid="converter-5k" />
        </div>
      )}
    </div>
  );
};

const TrainingZones = () => {
  const [time, setTime] = useState("");
  const sec = parseTime(time);
  const zones = sec ? trainingZonesFrom5k(sec) : null;
  return (
    <div>
      <span className={label}>RECENT 5K TIME</span>
      <input data-testid="zones-time-input" placeholder="25:00" value={time} onChange={(e) => setTime(e.target.value)} className={`${inputCls} max-w-xs`} />
      {zones && (
        <div className="overflow-x-auto mt-10 border border-white/15" data-testid="zones-results">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white text-ink">
                <th className="text-left font-mono-label px-4 py-3">ZONE</th>
                <th className="text-left font-mono-label px-4 py-3">PER MILE</th>
                <th className="text-left font-mono-label px-4 py-3">PER KM</th>
                <th className="text-left font-mono-label px-4 py-3 hidden md:table-cell">FEELS LIKE</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.zone} className="border-t border-white/10">
                  <td className="px-4 py-3 font-semibold text-white/90">{z.zone}</td>
                  <td className="px-4 py-3 text-white/60">{fmtPace(z.perMi[1])} – {fmtPace(z.perMi[0])}</td>
                  <td className="px-4 py-3 text-white/60">{fmtPace(z.perKm[1])} – {fmtPace(z.perKm[0])}</td>
                  <td className="px-4 py-3 text-white/50 hidden md:table-cell">{z.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const HyroxSplits = () => {
  const [time, setTime] = useState("");
  const [share, setShare] = useState(52);
  const sec = parseTime(time);
  const runBudget = sec ? sec * (1 - share / 100) : null;
  const perK = runBudget ? runBudget / 8 : null;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <span className={label}>TARGET FINISH TIME (H:MM:SS)</span>
          <input data-testid="hyrox-time-input" placeholder="1:30:00" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
        <div>
          <span className={label}>STATIONS + ROXZONE SHARE: {share}%</span>
          <input data-testid="hyrox-share-slider" type="range" min="40" max="65" value={share} onChange={(e) => setShare(+e.target.value)} className="w-full accent-white" />
        </div>
      </div>
      {perK && (
        <div className="mt-10" data-testid="hyrox-results">
          <ResultRow k="TOTAL RUN BUDGET (8 KM)" v={fmtTime(runBudget)} testid="hyrox-run-budget" />
          <ResultRow k="AVERAGE PER 1K RUN" v={fmtPace(perK)} testid="hyrox-per-km" />
          <ResultRow k="EQUIVALENT MILE PACE" v={`${fmtPace(perK * 1.60934)} /mi`} testid="hyrox-mile-pace" />
          <p className="text-white/40 text-xs mt-6">
            Typical athletes spend 48 to 58 percent of race time on stations and roxzone transitions. Even 1K splits assumed. Pace run one conservatively.
          </p>
        </div>
      )}
    </div>
  );
};

const MileageProgression = () => {
  const [current, setCurrent] = useState("");
  const [target, setTarget] = useState("");
  const [weeks, setWeeks] = useState("8");
  const plan = current && target && weeks ? mileageProgression(+current, +target, Math.min(+weeks, 24)) : null;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <span className={label}>CURRENT WEEKLY MILES</span>
          <input data-testid="mileage-current-input" type="number" min="1" placeholder="15" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
        </div>
        <div>
          <span className={label}>TARGET WEEKLY MILES</span>
          <input data-testid="mileage-target-input" type="number" min="1" placeholder="30" value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls} />
        </div>
        <div>
          <span className={label}>WEEKS (MAX 24)</span>
          <input data-testid="mileage-weeks-input" type="number" min="2" max="24" value={weeks} onChange={(e) => setWeeks(e.target.value)} className={inputCls} />
        </div>
      </div>
      {plan && (
        <div className="mt-10" data-testid="mileage-results">
          {plan.map((w) => (
            <div key={w.week} className="flex items-center justify-between border-b border-white/10 py-3">
              <span className="font-mono-label text-white/50">WEEK {w.week}{w.down ? " · DOWN WEEK" : ""}</span>
              <div className="flex items-center gap-4 flex-1 max-w-md ml-6">
                <div className="h-1.5 bg-white/10 flex-1">
                  <div className="h-full bg-white" style={{ width: `${Math.min(100, (w.mileage / Math.max(+target, +current)) * 100)}%` }} />
                </div>
                <span className="font-display text-xl w-16 text-right">{w.mileage}</span>
              </div>
            </div>
          ))}
          <p className="text-white/40 text-xs mt-6">Built on the ~10 percent rule with a recovery week every fourth week. Cap increases if anything hurts.</p>
        </div>
      )}
    </div>
  );
};

const ClubQuiz = () => {
  const [answers, setAnswers] = useState({});
  const done = Object.keys(answers).length === QUIZ.length;
  let result = null;
  if (done) {
    const tally = {};
    Object.values(answers).forEach((v) => (tally[v] = (tally[v] || 0) + 1));
    result = Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0];
  }
  return (
    <div>
      {QUIZ.map((q, i) => (
        <div key={q.key} className="border-b border-white/10 py-8">
          <p className="font-display text-xl lg:text-2xl mb-5">
            <span className="font-mono-label text-white/30 mr-3">0{i + 1}</span>
            {q.q}
          </p>
          <div className="flex flex-wrap gap-2">
            {q.options.map((o, j) => (
              <button
                key={o.label}
                data-testid={`quiz-q${i}-option-${j}`}
                onClick={() => setAnswers({ ...answers, [q.key]: o.value })}
                className={`font-mono-label px-4 py-2.5 border transition-colors duration-200 ${
                  answers[q.key] === o.value ? "bg-white text-ink border-white" : "border-white/30 hover:border-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      {done && (
        <div className="mt-10 border border-white p-8" data-testid="quiz-result">
          <p className="font-mono-label text-white/40">YOUR MATCH</p>
          <p className="font-display text-3xl lg:text-4xl mt-3">LOOK FOR “{result.toUpperCase()}” CLUBS.</p>
          <Link to={`/clubs?tag=${encodeURIComponent(result)}`} data-testid="quiz-directory-link" className="btn-invert inline-block mt-6 bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white">
            SEE MATCHING CLUBS
          </Link>
        </div>
      )}
    </div>
  );
};

const HeartRateZones = () => {
  const [age, setAge] = useState("");
  const [rest, setRest] = useState("");
  const a = parseInt(age, 10);
  const r = rest ? parseInt(rest, 10) : null;
  const data = a >= 10 && a <= 100 ? hrZones(a, r) : null;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <span className={label}>AGE</span>
          <input data-testid="hr-age-input" type="number" min="10" max="100" placeholder="32" value={age} onChange={(e) => setAge(e.target.value)} className={inputCls} />
        </div>
        <div>
          <span className={label}>RESTING HR (OPTIONAL, BPM)</span>
          <input data-testid="hr-rest-input" type="number" min="25" max="110" placeholder="58" value={rest} onChange={(e) => setRest(e.target.value)} className={inputCls} />
        </div>
      </div>
      {data && (
        <div className="mt-10" data-testid="hr-results">
          <ResultRow k="ESTIMATED MAX HR (TANAKA)" v={`${data.max} bpm`} testid="hr-max" />
          <div className="overflow-x-auto mt-8 border border-white/15">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white text-ink">
                  <th className="text-left font-mono-label px-4 py-3">ZONE</th>
                  <th className="text-left font-mono-label px-4 py-3">BPM RANGE</th>
                  <th className="text-left font-mono-label px-4 py-3 hidden md:table-cell">FEELS LIKE</th>
                </tr>
              </thead>
              <tbody>
                {data.zones.map((z) => (
                  <tr key={z.zone} className="border-t border-white/10">
                    <td className="px-4 py-3 font-semibold text-white/90">{z.zone}</td>
                    <td className="px-4 py-3 text-white/60">{z.bpm[0]} – {z.bpm[1]}</td>
                    <td className="px-4 py-3 text-white/50 hidden md:table-cell">{z.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-white/40 text-xs mt-6">Method: {data.method}. Add your resting heart rate for tighter, personalized zones via the Karvonen formula.</p>
        </div>
      )}
    </div>
  );
};

const CaloriesBurned = () => {
  const [weight, setWeight] = useState("");
  const [wUnit, setWUnit] = useState("lb");
  const [dist, setDist] = useState("");
  const [dUnit, setDUnit] = useState("mi");
  const [time, setTime] = useState("");
  const kg = (parseFloat(weight) || 0) * (wUnit === "lb" ? 0.453592 : 1);
  const km = (parseFloat(dist) || 0) * (dUnit === "mi" ? 1.60934 : 1);
  const sec = parseTime(time);
  const res = kg > 20 && km > 0 ? runCalories(kg, km, sec) : null;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <span className={label}>BODY WEIGHT</span>
          <div className="flex gap-3">
            <input data-testid="cal-weight-input" type="number" min="0" placeholder="160" value={weight} onChange={(e) => setWeight(e.target.value)} className={inputCls} />
            <select data-testid="cal-weight-unit" value={wUnit} onChange={(e) => setWUnit(e.target.value)} className={selectCls}>
              <option value="lb">LB</option>
              <option value="kg">KG</option>
            </select>
          </div>
        </div>
        <div>
          <span className={label}>DISTANCE RUN</span>
          <div className="flex gap-3">
            <input data-testid="cal-distance-input" type="number" min="0" step="0.1" placeholder="5" value={dist} onChange={(e) => setDist(e.target.value)} className={inputCls} />
            <select data-testid="cal-distance-unit" value={dUnit} onChange={(e) => setDUnit(e.target.value)} className={selectCls}>
              <option value="mi">MI</option>
              <option value="km">KM</option>
            </select>
          </div>
        </div>
        <div>
          <span className={label}>TIME (OPTIONAL)</span>
          <input data-testid="cal-time-input" placeholder="45:00" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
      </div>
      {res && (
        <div className="mt-10" data-testid="cal-results">
          <ResultRow k="TOTAL CALORIES BURNED" v={`~${res.gross} kcal`} testid="cal-gross" />
          <ResultRow k="NET CALORIES (ABOVE RESTING)" v={`~${res.net} kcal`} testid="cal-net" />
          <ResultRow k="PER MILE" v={`~${res.perMile} kcal`} testid="cal-per-mile" />
          {res.perHour && <ResultRow k="BURN RATE" v={`~${res.perHour} kcal/hr`} testid="cal-per-hour" />}
          <p className="text-white/40 text-xs mt-6">
            Based on the standard research rule of roughly 0.75 kcal per pound per mile (net 0.63 above resting burn). Terrain, wind, and running economy shift the real number, treat this as a solid estimate, not a lab test.
          </p>
        </div>
      )}
    </div>
  );
};

const SplitCalculator = () => {
  const [dist, setDist] = useState("Half Marathon");
  const [time, setTime] = useState("");
  const [unit, setUnit] = useState("mi");
  const [strategy, setStrategy] = useState("even");
  const sec = parseTime(time);
  const km = DISTANCES[dist];
  const splits = sec && km ? raceSplits(sec, km, unit, strategy) : null;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div>
          <span className={label}>RACE</span>
          <select data-testid="split-distance-select" value={dist} onChange={(e) => setDist(e.target.value)} className={`${selectCls} w-full`}>
            {Object.keys(DISTANCES).map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <span className={label}>GOAL TIME</span>
          <input data-testid="split-time-input" placeholder="1:45:00" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
        <div>
          <span className={label}>SPLITS PER</span>
          <select data-testid="split-unit-select" value={unit} onChange={(e) => setUnit(e.target.value)} className={`${selectCls} w-full`}>
            <option value="mi">MILE</option>
            <option value="km">KILOMETER</option>
          </select>
        </div>
        <div>
          <span className={label}>STRATEGY</span>
          <select data-testid="split-strategy-select" value={strategy} onChange={(e) => setStrategy(e.target.value)} className={`${selectCls} w-full`}>
            <option value="even">EVEN SPLITS</option>
            <option value="negative">NEGATIVE SPLIT</option>
            <option value="positive">POSITIVE SPLIT</option>
          </select>
        </div>
      </div>
      {splits && (
        <div className="overflow-x-auto mt-10 border border-white/15" data-testid="split-results">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white text-ink">
                <th className="text-left font-mono-label px-4 py-3">SPLIT</th>
                <th className="text-left font-mono-label px-4 py-3">TIME</th>
                <th className="text-left font-mono-label px-4 py-3">PACE</th>
                <th className="text-left font-mono-label px-4 py-3">ELAPSED</th>
              </tr>
            </thead>
            <tbody>
              {splits.map((s, i) => (
                <tr key={s.label} className="border-t border-white/10" data-testid={`split-row-${i}`}>
                  <td className="px-4 py-2.5 font-semibold text-white/90">{s.label}</td>
                  <td className="px-4 py-2.5 text-white/60">{fmtTime(s.split)}</td>
                  <td className="px-4 py-2.5 text-white/60">{fmtPace(s.pace)} /{unit}</td>
                  <td className="px-4 py-2.5 text-white/60">{fmtTime(s.cumulative)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {splits && (
        <p className="text-white/40 text-xs mt-6">
          Negative split runs the second half about 2 percent faster than the first, the way most distance PRs actually happen. Positive split front-loads the effort, useful for hot races or hilly back halves.
        </p>
      )}
    </div>
  );
};

const RaceFueling = () => {
  const [weight, setWeight] = useState("");
  const [wUnit, setWUnit] = useState("lb");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [hUnit, setHUnit] = useState("ftin");
  const [duration, setDuration] = useState("");
  const kg = (parseFloat(weight) || 0) * (wUnit === "lb" ? 0.453592 : 1);
  const cm = hUnit === "cm" ? parseFloat(heightCm) || 0 : ((parseFloat(heightFt) || 0) * 12 + (parseFloat(heightIn) || 0)) * 2.54;
  const sec = parseTime(duration);
  const bmi = kg > 20 && cm > 100 ? bmiOf(kg, cm) : null;
  const plan = sec && kg > 20 ? fuelingPlan(sec / 3600, kg) : null;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <span className={label}>BODY WEIGHT</span>
          <div className="flex gap-3">
            <input data-testid="fuel-weight-input" type="number" min="0" placeholder="160" value={weight} onChange={(e) => setWeight(e.target.value)} className={inputCls} />
            <select data-testid="fuel-weight-unit" value={wUnit} onChange={(e) => setWUnit(e.target.value)} className={selectCls}>
              <option value="lb">LB</option>
              <option value="kg">KG</option>
            </select>
          </div>
        </div>
        <div>
          <span className={label}>HEIGHT</span>
          {hUnit === "ftin" ? (
            <div className="flex gap-3">
              <input data-testid="fuel-height-ft" type="number" min="0" max="8" placeholder="FT" value={heightFt} onChange={(e) => setHeightFt(e.target.value)} className={inputCls} />
              <input data-testid="fuel-height-in" type="number" min="0" max="11" placeholder="IN" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} className={inputCls} />
              <select data-testid="fuel-height-unit" value={hUnit} onChange={(e) => setHUnit(e.target.value)} className={selectCls}>
                <option value="ftin">FT/IN</option>
                <option value="cm">CM</option>
              </select>
            </div>
          ) : (
            <div className="flex gap-3">
              <input data-testid="fuel-height-cm" type="number" min="0" placeholder="175" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} className={inputCls} />
              <select value={hUnit} onChange={(e) => setHUnit(e.target.value)} className={selectCls}>
                <option value="ftin">FT/IN</option>
                <option value="cm">CM</option>
              </select>
            </div>
          )}
        </div>
        <div>
          <span className={label}>EXPECTED RACE TIME (H:MM:SS)</span>
          <input data-testid="fuel-duration-input" placeholder="1:50:00" value={duration} onChange={(e) => setDuration(e.target.value)} className={inputCls} />
        </div>
      </div>
      {bmi && (
        <div className="mt-10" data-testid="fuel-bmi-results">
          <ResultRow k="BMI" v={bmi.bmi.toFixed(1)} testid="fuel-bmi" />
          <ResultRow k="CATEGORY" v={bmi.cat} testid="fuel-bmi-cat" />
        </div>
      )}
      {plan && (
        <div className="mt-8" data-testid="fuel-plan-results">
          <ResultRow k="CARBS PER HOUR" v={`${plan.carbsPerHour[0]}–${plan.carbsPerHour[1]} g`} testid="fuel-carbs-hr" />
          <ResultRow k="TOTAL RACE CARBS" v={`${plan.totalCarbs[0]}–${plan.totalCarbs[1]} g`} testid="fuel-carbs-total" />
          <ResultRow k="GELS (~25G EACH)" v={`${plan.gels[0]}–${plan.gels[1]}`} testid="fuel-gels" />
          <ResultRow k="FLUID PER HOUR" v={`${plan.fluidPerHour[0]}–${plan.fluidPerHour[1]} ml`} testid="fuel-fluid" />
          <ResultRow k="SODIUM PER HOUR" v={`${plan.sodiumPerHour[0]}–${plan.sodiumPerHour[1]} mg`} testid="fuel-sodium" />
          {plan.carbLoad && <ResultRow k="CARB LOAD (PER DAY, 36-48H OUT)" v={`${plan.carbLoad[0]}–${plan.carbLoad[1]} g`} testid="fuel-carb-load" />}
          <p className="text-white/40 text-xs mt-6">
            Ranges follow standard sports nutrition guidance: 30 to 60 g carbs per hour for efforts over about 75 minutes, up to 90 g with mixed glucose-fructose sources for long races. Practice your fueling in training, race day is not the experiment. BMI is a population screen, not a runner's body composition test.
          </p>
        </div>
      )}
    </div>
  );
};

const TOOL_COMPONENTS = {
  "pace-calculator": PaceCalculator,
  "race-predictor": RacePredictor,
  "pace-converter": PaceConverter,
  "training-zones": TrainingZones,
  "heart-rate-zones": HeartRateZones,
  "calories-burned": CaloriesBurned,
  "split-calculator": SplitCalculator,
  "race-fueling": RaceFueling,
  "hyrox-split-calculator": HyroxSplits,
  "mileage-progression": MileageProgression,
  "club-finder-quiz": ClubQuiz,
};

const TOOL_NOTES = {
  "pace-calculator": "Enter any race distance and finish time to get pace per mile, per kilometer, and speed. Useful for setting race goals and checking pace group fit before joining a club run.",
  "race-predictor": "Predictions use the Riegel formula (exponent 1.06), the standard for equivalent race performances. Accuracy assumes you have trained appropriately for the target distance. Marathon predictions from a 5K skew optimistic.",
  "pace-converter": "Instant translation between min/mile, min/km, and mph. Handy when your club posts paces in a unit your watch does not use.",
  "training-zones": "Zones derived from your current 5K pace using multipliers consistent with common threshold-based systems. They are starting points, adjust with feel and, ideally, a coach.",
  "hyrox-split-calculator": "HYROX is roughly half running. This tool splits your target time into a run budget across the eight 1K runs. Station share varies by athlete strength, adjust the slider to match yours.",
  "mileage-progression": "A conservative build using the ~10 percent weekly increase rule with a down week every fourth week. Not medical advice. If pain changes your gait, stop.",
  "club-finder-quiz": "Four questions map you to the club tags in our directory. There is no wrong answer and no algorithm pretending to know you better than you do.",
  "heart-rate-zones": "Five training zones from your age using the Tanaka max-HR formula (208 minus 0.7 x age). Add your resting heart rate and it switches to the more personal Karvonen heart-rate-reserve method.",
  "calories-burned": "Weight times distance is what actually drives running calorie burn, pace matters far less than people think. Add your time to see burn rate per hour.",
  "split-calculator": "Turn a goal time into a printable split table: even, negative, or positive split strategy, per mile or per kilometer, with elapsed time at every marker.",
  "race-fueling": "BMI plus an evidence-based race fueling plan: carbs per hour, total gels, fluid, and sodium for your expected finish time. Longer than 90 minutes gets a carb-load target too.",
};

// Audit NRC-040: related content links for the 11 native tools
const NATIVE_LINKS = {
  "pace-calculator": [
    { label: "Race Time Predictor", path: "/tools/race-predictor" },
    { label: "Speed work training guides", path: "/training/category/speed" },
    { label: "Build a full training plan", path: "/plan" },
  ],
  "race-predictor": [
    { label: "Race Split Calculator", path: "/tools/split-calculator" },
    { label: "Marathon training guides", path: "/training/category/marathon" },
    { label: "Build a full training plan", path: "/plan" },
  ],
  "pace-converter": [
    { label: "Running Pace Calculator", path: "/tools/pace-calculator" },
    { label: "Training Zone Calculator", path: "/tools/training-zones" },
  ],
  "training-zones": [
    { label: "Heart Rate Zone Calculator", path: "/tools/heart-rate-zones" },
    { label: "Training guides", path: "/training/category/training" },
    { label: "Build a full training plan", path: "/plan" },
  ],
  "heart-rate-zones": [
    { label: "Heart rate zones, explained properly", path: "/training/heart-rate-zones-for-runners" },
    { label: "Training Zone Calculator (pace-based)", path: "/tools/training-zones" },
  ],
  "calories-burned": [
    { label: "Runner TDEE Calculator", path: "/tools/runner-tdee-calculator" },
    { label: "Nutrition guides for runners", path: "/training/category/nutrition" },
  ],
  "split-calculator": [
    { label: "Race Time Predictor", path: "/tools/race-predictor" },
    { label: "Marathon training guides", path: "/training/category/marathon" },
  ],
  "race-fueling": [
    { label: "Race Fueling Planner (full plan)", path: "/tools/race-fueling-planner" },
    { label: "Nutrition guides for runners", path: "/training/category/nutrition" },
  ],
  "hyrox-split-calculator": [
    { label: "HYROX Finish Time Predictor", path: "/tools/hyrox-finish-time" },
    { label: "HYROX training guides", path: "/training/category/hyrox" },
  ],
  "mileage-progression": [
    { label: "Build a full training plan", path: "/plan" },
    { label: "Training guides", path: "/training/category/training" },
  ],
  "club-finder-quiz": [
    { label: "How to choose a run club", path: "/training/how-to-choose-a-run-club" },
    { label: "The 2026 club rankings", path: "/ranked" },
    { label: "Browse the directory", path: "/clubs" },
  ],
};

export default function ToolPage() {
  const { toolSlug } = useParams();
  const resultRef = useRef(null);
  const meta = TOOL_LIST.find(([slug]) => slug === toolSlug);
  const Tool = TOOL_COMPONENTS[toolSlug];
  const PackTool = PACK_COMPONENTS[toolSlug];
  const packEntry = PackTool ? TOOLS_MANIFEST.find((m) => m.route === `/tools/${toolSlug}`) : null;
  const toolName = packEntry?.h1 || meta?.[1];

  useEffect(() => {
    if (packEntry) setPageMeta(packEntry.title, packEntry.meta_description);
    else if (meta) setPageMeta(`${meta[1]} | Free Running Tool | Ninja Run Club`, meta[2]);
  }, [meta, packEntry]);

  // Audit NRC-063: SoftwareApplication structured data on every tool
  useEffect(() => {
    if (!toolName) return;
    return setJsonLd("tool-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: toolName,
      applicationCategory: "HealthApplication",
      operatingSystem: "Web",
      url: `${SITE_BASE}/tools/${toolSlug}`,
      description: packEntry?.meta_description || meta?.[2],
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Ninja Run Club", url: SITE_BASE },
    });
  }, [toolSlug, toolName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (PackTool) {
    return (
      <div className="bg-night min-h-screen">
        <div className="px-5 lg:px-10 pt-10">
          <Link to="/tools" className="inline-flex items-center gap-2 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
            <ArrowLeft size={13} /> ALL TOOLS
          </Link>
        </div>
        <div data-testid={`tool-${toolSlug}`} ref={resultRef}>
          <Suspense fallback={<p className="font-mono-label text-white/40 text-center py-24">LOADING THE TOOL...</p>}>
            <PackTool />
          </Suspense>
        </div>
        <div className="px-5 lg:px-10 pb-16 max-w-5xl mx-auto">
          <div className="light-section mb-10">
            <ToolEmailCapture tool={toolSlug} toolName={toolName} resultRef={resultRef} />
          </div>
          {packEntry?.internal_links?.length > 0 && (
            <>
              <h2 className="font-mono-label text-white/40 mb-4">GO DEEPER</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {packEntry.internal_links.map((l) => (
                  <Link key={l.path} to={l.path} className="group bg-night p-6 flex items-center justify-between gap-3 hover:bg-white hover:text-ink transition-colors duration-300">
                    <span className="text-sm">{l.label}</span>
                    <ArrowRight size={14} className="opacity-40 group-hover:opacity-100 shrink-0" />
                  </Link>
                ))}
              </div>
            </>
          )}
          <p className="font-mono-label text-white/30 mt-10">FOR TRAINING GUIDANCE, NOT MEDICAL ADVICE. LISTEN TO YOUR BODY FIRST.</p>
        </div>
      </div>
    );
  }

  if (!Tool) {
    setPageMeta("Tool Not Found | Ninja Run Club", "", { noindex: true });
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5">
        <p className="font-display text-4xl">TOOL NOT FOUND.</p>
        <Link to="/tools" className="font-mono-label link-underline text-white/60">ALL TOOLS</Link>
      </div>
    );
  }

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-14 pb-10 border-b border-white/10 bg-ink">
        <Link to="/tools" className="inline-flex items-center gap-2 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
          <ArrowLeft size={13} /> ALL TOOLS
        </Link>
        <h1 data-testid="tool-title" className="font-display text-3xl sm:text-4xl lg:text-6xl mt-8 leading-none">{meta[1].toUpperCase()}</h1>
      </header>
      <div className="px-5 lg:px-10 py-12 max-w-4xl" data-testid={`tool-${toolSlug}`}>
        <div ref={resultRef}>
          <Tool />
        </div>
        <section className="mt-12">
          <h2 className="font-mono-label text-white/40 mb-3">HOW TO USE THIS</h2>
          <p className="text-white/50 text-sm max-w-2xl">{TOOL_NOTES[toolSlug]}</p>
        </section>
        <div className="light-section mt-10">
          <ToolEmailCapture tool={toolSlug} toolName={meta[1]} resultRef={resultRef} />
        </div>
        <div className="mt-6">
          <GoDeeper links={NATIVE_LINKS[toolSlug]} />
        </div>
        <p className="font-mono-label text-white/30 mt-14">
          FOR TRAINING GUIDANCE, NOT MEDICAL ADVICE. LISTEN TO YOUR BODY FIRST.
        </p>
      </div>
    </div>
  );
}
